import React, { useState } from "react";
import {
  Layout,
  Space,
  Dropdown,
  Avatar,
  Button,
  Grid,
  Tooltip,
  theme,
} from "antd";
import type { MenuProps } from "antd";
import {
  UserOutlined,
  LogoutOutlined,
  DownOutlined,
  MenuOutlined,
  ReloadOutlined,
  SunOutlined,
  MoonOutlined,
  BookOutlined,
} from "@ant-design/icons";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { SiteSelector } from "./SiteSelector";
import { HelpButton } from "../features/userGuide";
import { useGetIdentity, useLogout } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMobileMenu } from "../contexts/MobileContext";
import { useTheme } from "../contexts/ThemeContext";
import { ENV } from "../config/env";

const { useBreakpoint } = Grid;

export const Header: React.FC = () => {
  const { t } = useTranslation();
  const { data: identity } = useGetIdentity<{ name: string }>();
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const { toggleMobileMenu } = useMobileMenu();
  const { isDark, toggleTheme } = useTheme();
  const { token } = theme.useToken();

  const isMobile = !screens.md;
  const showUserName = screens.sm;
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const accessToken = localStorage.getItem("access_token");
      await fetch(`${ENV.API_URL}/cache`, {
        method: "DELETE",
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
    } catch {
      // Ignore errors - reload will clear frontend cache regardless
    }
    window.location.reload();
  };

  const menuItems: MenuProps["items"] = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: t("profile.title"),
      onClick: () => navigate("/profile"),
    },
    {
      type: "divider",
    },
    {
      key: "version",
      label: (
        <span
          style={{
            color: token.colorTextQuaternary,
            fontSize: 12,
            cursor: "default",
          }}
        >
          {t("common.version")} {ENV.APP_VERSION}
        </span>
      ),
      disabled: true,
      style: { cursor: "default", padding: "4px 12px", minHeight: "auto" },
    },
    {
      type: "divider",
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: t("auth.logout"),
      onClick: () => logout(),
      danger: true,
    },
  ];

  return (
    <Layout.Header
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: isMobile ? "0 12px" : "0 24px",
        backgroundColor: token.colorBgContainer,
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        height: 64,
      }}
    >
      {/* Left side - Hamburger menu on mobile */}
      <div style={{ display: "flex", alignItems: "center" }}>
        {isMobile && (
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={toggleMobileMenu}
            aria-label="menu"
            style={{
              fontSize: "18px",
              width: 40,
              height: 40,
            }}
          />
        )}
      </div>

      {/* Right side - Site selector, Help, Theme toggle, Language switcher and user dropdown */}
      <Space size={isMobile ? 2 : "middle"} style={{ flexShrink: 0 }}>
        <SiteSelector compact={isMobile} />
        <Tooltip title={t("buttons.refresh")} placement="bottom">
          <Button
            type="text"
            icon={<ReloadOutlined spin={refreshing} />}
            onClick={handleRefresh}
            loading={refreshing}
            aria-label={t("buttons.refresh")}
            style={{
              fontSize: isMobile ? "16px" : "18px",
              width: isMobile ? 32 : 40,
              height: isMobile ? 32 : 40,
            }}
          />
        </Tooltip>
        {!isMobile && ENV.USER_MANUAL_ENABLED && (
          <Tooltip title={t("navigation.manuals")} placement="bottom">
            <Button
              type="text"
              icon={<BookOutlined />}
              onClick={() => navigate("/manuals")}
              aria-label={t("navigation.manuals")}
              style={{
                fontSize: "18px",
                width: 40,
                height: 40,
              }}
            />
          </Tooltip>
        )}
        {!isMobile && ENV.HELP_CENTER_ENABLED && <HelpButton />}
        <Tooltip
          title={t("theme.toggle", "Toggle dark mode")}
          placement="bottom"
        >
          <Button
            type="text"
            icon={isDark ? <SunOutlined /> : <MoonOutlined />}
            onClick={toggleTheme}
            aria-label={t("theme.toggle", "Toggle dark mode")}
            style={{
              fontSize: isMobile ? "16px" : "18px",
              width: isMobile ? 32 : 40,
              height: isMobile ? 32 : 40,
            }}
          />
        </Tooltip>
        <LanguageSwitcher />
        <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
          <Button type="text" style={isMobile ? { padding: "0 4px" } : undefined}>
            <Space size="small">
              <Avatar
                size="small"
                icon={<UserOutlined />}
                style={{ backgroundColor: token.colorPrimary }}
              />
              {identity?.name && showUserName && <span>{identity.name}</span>}
              {!isMobile && <DownOutlined style={{ fontSize: 10 }} />}
            </Space>
          </Button>
        </Dropdown>
      </Space>
    </Layout.Header>
  );
};
